import {
  Component,
  ChangeDetectionStrategy,
  InputSignal,
  input,
} from '@angular/core';
import { StatusColum } from '@shared/components/simple-table/models/simple-table-model';
import { NgIf } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { ColumnStatusPipe } from '@shared/components/simple-table/components/status-column/pipes/column-status.pipe';

@Component({
    selector: 'app-status-column',
    templateUrl: './status-column.component.html',
    styleUrls: ['./status-column.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgIf, MatIcon, TranslatePipe, ColumnStatusPipe]
})
export class StatusColumnComponent {
  /**
   * Receive a object with the status and configurations for each one for translating it
   * status = analise | 1933
   * @example
   * statusConfiguration: {
   *   analise: {
   *    color: 'red',
   *    description: 'Analise'
   *   },
   *   aprovado: {
   *    color: 'green',
   *    description: 'Aprovado'
   *   },
   * }
   *
   */

  readonly config: InputSignal<{
    [key: string]: StatusColum;
  }> = input<{
    [key: string]: StatusColum;
  }>({
    analise: {
      color: '#00b8ef',
      description: 'Análise',
      icon: 'search',
    },
  });

  readonly value: InputSignal<string> = input<string>('analise');
  readonly filled: InputSignal<boolean> = input<boolean>(false);

  getBackgroundColor(color: string): string {
    return this.toRgba(color, this.filled() ? 0.18 : 0.1);
  }

  getTextColor(color: string): string {
    return this.darkenColor(color, this.filled() ? 12 : 4);
  }

  getBorderColor(color: string): string {
    if (!color) return 'none';
    return `1px solid ${this.toRgba(this.darkenColor(color, this.filled() ? 8 : 0), this.filled() ? 0.24 : 0.16)}`;
  }

  private darkenColor(color: string, percent: number): string {
    if (!color) return color;
    let r = 0, g = 0, b = 0;

    if (color.startsWith('#')) {
      const hex = color.substring(1);
      if (hex.length === 3) {
        r = parseInt(hex[0] + hex[0], 16);
        g = parseInt(hex[1] + hex[1], 16);
        b = parseInt(hex[2] + hex[2], 16);
      } else if (hex.length >= 6) {
        r = parseInt(hex.substring(0, 2), 16);
        g = parseInt(hex.substring(2, 4), 16);
        b = parseInt(hex.substring(4, 6), 16);
      }
    } else if (color.startsWith('rgb')) {
      // Simple extraction for rgb/rgba
      const match = color.match(/\d+/g);
      if (match && match.length >= 3) {
        r = parseInt(match[0]);
        g = parseInt(match[1]);
        b = parseInt(match[2]);
      }
    } else {
      return color;
    }

    r = Math.floor(r * (100 - percent) / 100);
    g = Math.floor(g * (100 - percent) / 100);
    b = Math.floor(b * (100 - percent) / 100);

    return `rgb(${r}, ${g}, ${b})`;
  }

  private toRgba(color: string, alpha: number): string {
    if (!color) {
      return `rgba(148, 163, 184, ${alpha})`;
    }

    if (/^#([A-Fa-f0-9]{3,8})$/.test(color)) {
      const hex = color.slice(1);
      const normalized = hex.length === 3
        ? hex.split('').map(char => char + char).join('')
        : hex.length >= 6
          ? hex.slice(0, 6)
          : hex;
      const r = parseInt(normalized.slice(0, 2), 16);
      const g = parseInt(normalized.slice(2, 4), 16);
      const b = parseInt(normalized.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    if (color.startsWith('rgb')) {
      const match = color.match(/\d+/g);
      if (match && match.length >= 3) {
        return `rgba(${match[0]}, ${match[1]}, ${match[2]}, ${alpha})`;
      }
    }

    return color;
  }
}
